import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';

/**
 * ============================================================================
 * ModularGridBlock - Grille Modulaire Configurable
 * ============================================================================
 * 
 * DUAL MODE LAYOUT SYSTEM (v2.1):
 * 
 * MODE "auto" (DEFAULT - backward compatible):
 *   - Hauteur des cellules = contenu adaptatif
 *   - grid-auto-rows: auto
 *   - Comportement actuel inchangé
 * 
 * MODE "fixed":
 *   - Hauteur uniforme contrôlée (via row_height prop ou CSS var)
 *   - grid-auto-rows: var(--grid-row-height, 160px)
 *   - Cellules avec overflow: hidden (pour images)
 *   - Images: object-contain + center (pas de crop)
 *   - Texte: affichage COMPLET sans line-clamp ni ellipsis
 * 
 * CONTENT FIT POLICY (v2.1):
 * ✅ Images: object-fit: contain (préserve ratio, pas de crop)
 * ✅ Texte: affichage naturel, wrap, SANS troncature "..."
 * ✅ CMS blocks: flex adaptatif, min-height: 0
 * 
 * DESIGN SYSTEM CONTRACT:
 * ✅ width: 100% du parent (le layout contrôle la largeur)
 * ✅ padding interne configurable
 * ✅ styles internes (grid layout, cell styles)
 * ❌ INTERDIT: max-width basé sur viewport
 * ❌ INTERDIT: width en % du viewport
 * ❌ INTERDIT: margin auto pour contrôler la largeur globale
 * 
 * RÈGLE: Le LAYOUT parent définit la largeur, pas le bloc.
 * 
 * BACKWARD COMPATIBILITY:
 * - Si mode non défini → mode = "auto"
 * - cell_height / row_heights → active automatiquement le comportement fixed
 */

// 🔥 HELPER FUNCTION SÉCURISÉE
const safeNumber = (value, fallback = 0) => 
  typeof value === 'number' ? value : fallback;

// 🔥 MODE CONSTANTS
const GRID_MODES = {
  AUTO: 'auto',
  FIXED: 'fixed'
};

const ModularGridBlock = ({ block }) => {
  const navigate = useNavigate();
  
  // 🔥 1. INITIALISATION SÉCURISÉE (CRITIQUE)
  const content = block?.content || {};
  
  const internalPadding = content.internal_padding ?? 0;
  const gap = content.gap ?? 0;
  const cells = content.cells || [];
  const columns = safeNumber(content.columns, 1);
  const rows = safeNumber(content.rows, 1);
  const title = content.title || '';
  const cellBorderRadius = safeNumber(content.cell_border_radius, 8);
  const cellHeight = safeNumber(content.cell_height, 0);
  const rowHeights = Array.isArray(content.row_heights) ? content.row_heights : [];
  
  // SECTION-LEVEL: fond global + padding de section
  const sectionBgColor = content.background_color || 'transparent';
  const sectionPadding = safeNumber(content.section_padding, 0);
  
  // 🔥 2. DUAL MODE SYSTEM - Détermination du mode
  // Règle: Si mode explicite → utiliser, sinon déduire du contenu
  const explicitMode = content.grid_mode; // "auto" | "fixed" | undefined
  
  // Auto-detection: si row_heights ou cell_height défini → comportement fixed implicite
  const hasHeightConfig = rowHeights.length > 0 || cellHeight > 0;
  
  // Mode final: explicite > implicite (hasHeightConfig) > fallback "auto"
  const gridMode = explicitMode || (hasHeightConfig ? GRID_MODES.FIXED : GRID_MODES.AUTO);
  
  // Row height pour mode fixed (configurable via content.row_height ou fallback 160px)
  const fixedRowHeight = safeNumber(content.row_height, 160);

  // 🔥 Fonction pour obtenir la hauteur d'une cellule selon sa ligne
  // Priorité: row_heights > cell_height > fixed default > auto (0)
  const getCellHeightForRow = (rowIndex) => {
    // Mode AUTO: toujours retourner 0 (auto-height)
    if (gridMode === GRID_MODES.AUTO && !hasHeightConfig) {
      return 0;
    }
    
    // 1. Vérifier row_heights (système avancé)
    if (rowHeights.length > 0 && rowHeights[rowIndex] !== undefined && rowHeights[rowIndex] > 0) {
      return rowHeights[rowIndex];
    }
    // 2. Fallback sur cell_height (système legacy)
    if (cellHeight > 0) {
      return cellHeight;
    }
    // 3. Mode FIXED explicite sans height config → utiliser fixedRowHeight
    if (gridMode === GRID_MODES.FIXED) {
      return fixedRowHeight;
    }
    // 4. Auto (0)
    return 0;
  };

  // 🔥 DEBUG TEMPORAIRE
  console.log('ModularGridBlock content:', {
    gridMode,
    explicitMode,
    hasHeightConfig,
    internalPadding,
    gap,
    cellsCount: cells.length,
    columns,
    rows,
    title,
    cellBorderRadius,
    cellHeight,
    rowHeights,
    fixedRowHeight
  });

  // 🔥 6. SÉCURITÉ ANTI-CRASH (ULTRA IMPORTANT)
  if (!Array.isArray(cells)) {
    console.warn('ModularGridBlock: cells is not an array');
    return null;
  }

  if (cells.length === 0) {
    console.warn('ModularGridBlock: No cells found in block');
    return null;
  }

  // Filter out disabled cells
  const activeCells = cells.filter(cell => !cell?.disabled);
  
  console.log('📊 Active cells:', activeCells.length, '/', cells.length);

  // 🔥 5. RESPONSIVE PROPRE
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const responsiveGap = gap === 0 ? 0 : gap * (isMobile ? 0.75 : 1);

  const handleButtonClick = (link) => {
    if (!link) return;
    
    if (link.startsWith('http://') || link.startsWith('https://')) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link);
    }
  };

  // 🔥 3. FIX PADDING CELLULE (PRIORITÉ CORRECTE)
  const getPaddingStyle = (cell, isMobileView = false) => {
    if (!cell) return `${internalPadding}px`;
    
    const paddingDetail = cell?.data?.padding_detail;

    if (paddingDetail) {
      if (isMobileView) {
        // Mobile: réduire de 25% sauf si 0
        const top = paddingDetail.top === 0 ? 0 : (paddingDetail.top || 0) * 0.75;
        const right = paddingDetail.right === 0 ? 0 : (paddingDetail.right || 0) * 0.75;
        const bottom = paddingDetail.bottom === 0 ? 0 : (paddingDetail.bottom || 0) * 0.75;
        const left = paddingDetail.left === 0 ? 0 : (paddingDetail.left || 0) * 0.75;
        return `${top}px ${right}px ${bottom}px ${left}px`;
      }
      
      // Desktop: valeurs exactes
      return `${paddingDetail.top || 0}px ${paddingDetail.right || 0}px ${paddingDetail.bottom || 0}px ${paddingDetail.left || 0}px`;
    }

    // Ancien format padding simple
    if (cell?.data?.padding) {
      const paddingMap = {
        small: isMobileView ? 6 : 8,
        medium: isMobileView ? 12 : 16,
        large: isMobileView ? 18 : 24
      };
      return `${paddingMap[cell.data.padding] || internalPadding}px`;
    }

    // Padding global
    const globalPadding = isMobileView 
      ? (internalPadding === 0 ? 0 : internalPadding * 0.75)
      : internalPadding;
    
    return `${globalPadding}px`;
  };

  // 🔥 Fonction pour déterminer si une hauteur fixe est active pour une cellule
  // Mode FIXED = toujours true, Mode AUTO = dépend de la config explicite
  const isFixedModeActive = gridMode === GRID_MODES.FIXED || hasHeightConfig;

  const renderCellContent = (cell, cellRowIndex = 0) => {
    if (!cell) return null;
    
    const { content_type, data } = cell;
    
    // Récupérer l'alignement de texte
    const textAlign = data?.text_align || 'left';
    
    // Déterminer si cette cellule a une hauteur fixe
    const effectiveHeight = getCellHeightForRow(cellRowIndex);
    const isFixedHeight = effectiveHeight > 0;
    
    // 🔥 FIX: Style adaptatif pour texte SANS troncature forcée
    // Le texte s'affiche naturellement et remplit l'espace disponible
    const adaptiveTextStyle = isFixedHeight ? {
      // Permet au texte de wrapper naturellement
      wordWrap: 'break-word',
      overflowWrap: 'anywhere',
      // PAS de overflow hidden ni ellipsis sur le texte lui-même
      whiteSpace: 'normal'
    } : {};

    switch (content_type) {
      case 'title':
        // 🔥 TEXT DENSITY: Titre avec hiérarchie visuelle optimisée
        return (
          <h2 
            className="font-heading font-bold modular-grid__title"
            style={{ 
              textAlign,
              wordWrap: 'break-word',
              overflowWrap: 'anywhere',
              whiteSpace: 'normal',
              // Densité: clamp géré par CSS, fallback inline
              fontSize: 'clamp(14px, 1.5vw, 18px)',
              lineHeight: 1.25
            }}
          >
            {data?.text || ''}
          </h2>
        );

      case 'text':
        // 🔥 TEXT DENSITY: Bloc texte avec distribution verticale optimisée
        return (
          <div 
            className="modular-grid__text-block flex flex-col" 
            style={{ 
              textAlign,
              gap: '0.5rem',
              flex: '1 1 auto',
              justifyContent: isFixedHeight ? 'center' : 'flex-start'
            }}
          >
            {data?.title && (
              <h3 
                className="font-semibold modular-grid__text-title"
                style={{
                  wordWrap: 'break-word',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'normal',
                  fontSize: 'clamp(13px, 1.3vw, 16px)',
                  lineHeight: 1.25
                }}
              >
                {data.title}
              </h3>
            )}
            {data?.text && (
              <p 
                className="text-muted-foreground modular-grid__text-description"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'anywhere',
                  // Densité typographique optimisée
                  fontSize: 'clamp(12px, 1.1vw, 14px)',
                  lineHeight: 1.3
                }}
              >
                {data.text}
              </p>
            )}
          </div>
        );

      case 'image':
        // DEBUG: Log pour diagnostic
        console.log('[DEBUG ModularGrid] Image cell:', {
          cellId: cell.id,
          dataKeys: Object.keys(data || {}),
          image_url: data?.image_url,
          uploaded_image_url: data?.uploaded_image_url,
          rawData: data
        });
        
        // 🔥 FIX: Priorité à uploaded_image_url car image_url peut être vide ''
        const imageSrc = data?.uploaded_image_url || data?.image_url || null;
        const imageLink = data?.imageLink;
        
        // FALLBACK UI: Au lieu de return null silencieux, afficher placeholder
        // 🔥 FIX: min-h-0 pour respecter contrainte cellule FIXED
        if (!imageSrc) {
          console.warn('⚠️ No image source found for cell:', cell.id);
          return (
            <div 
              className={`w-full h-full bg-slate-100 rounded flex items-center justify-center ${isFixedHeight ? 'min-h-0' : 'min-h-[80px]'}`}
              data-testid="image-placeholder"
            >
              <div className="text-center text-slate-400 p-2">
                <svg className="w-8 h-8 mx-auto mb-1 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs">Image non définie</span>
              </div>
            </div>
          );
        }
        
        // Container pour image en mode hauteur fixe
        const imageContainerStyle = isFixedHeight ? {
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        } : {};
        
        // 🔥 FIX: Style image adaptatif - CONTAIN pour préserver le ratio sans crop
        const imageStyle = isFixedHeight 
          ? { 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',      // ✅ CONTAIN au lieu de COVER
              objectPosition: 'center'
            }
          : {};
        
        const imageElement = (
          <div style={imageContainerStyle}>
            <img 
              src={imageSrc} 
              alt={data?.alt || ''} 
              className={`rounded ${isFixedHeight ? 'w-full h-full object-contain' : 'w-full h-auto'}`}
              style={imageStyle}
              onError={(e) => {
                console.error('❌ Image failed to load:', imageSrc);
                e.target.style.display = 'none';
              }}
            />
          </div>
        );
        
        // Si imageLink existe, wrapper l'image dans un lien
        if (imageLink && imageLink.trim()) {
          const isExternal = imageLink.startsWith('http://') || imageLink.startsWith('https://');
          
          if (isExternal) {
            return (
              <a 
                href={imageLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block hover:opacity-90 transition-opacity"
              >
                {imageElement}
              </a>
            );
          } else {
            // Lien interne (React Router)
            return (
              <a 
                href={imageLink} 
                className="block hover:opacity-90 transition-opacity"
              >
                {imageElement}
              </a>
            );
          }
        }
        
        // Pas de lien, retourner l'image simple
        return imageElement;

      case 'button':
        if (!data?.text) return null;
        
        return (
          <div style={{ textAlign }}>
            <Button
              onClick={() => handleButtonClick(data.link)}
              disabled={!data.link}
              className="w-full sm:w-auto"
            >
              {data.text}
            </Button>
          </div>
        );

      case 'video':
        const videoUrl = data?.video_url;
        const uploadedVideoUrl = data?.uploaded_video_url;
        
        if (!videoUrl && !uploadedVideoUrl) return null;
        
        // Si URL YouTube/Vimeo
        if (videoUrl) {
          let embedUrl = videoUrl;
          
          if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
            const videoId = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
            if (videoId) {
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            }
          } else if (videoUrl.includes('vimeo.com')) {
            const videoId = videoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
            if (videoId) {
              embedUrl = `https://player.vimeo.com/video/${videoId}`;
            }
          }
          
          return (
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src={embedUrl}
                className="absolute top-0 left-0 w-full h-full rounded"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video embed"
              />
            </div>
          );
        }
        
        // Si vidéo uploadée
        if (uploadedVideoUrl) {
          return (
            <div className="relative w-full">
              <video 
                controls 
                className="w-full rounded"
                style={{ maxHeight: '400px' }}
              >
                <source src={uploadedVideoUrl} />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
          );
        }
        
        return null;

      case 'empty':
      default:
        return null;
    }
  };

  return (
    <div 
      className="modular-grid-block"
      data-testid="modular-grid-block"
      data-block-type="modular_grid"
      style={{
        // ============================================================
        // SECTION LEVEL: fond + padding de section CMS
        // ============================================================
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: sectionBgColor,
        padding: sectionPadding > 0 ? `${sectionPadding}px` : undefined,
        borderRadius: sectionPadding > 0 && sectionBgColor !== 'transparent' ? '8px' : undefined
      }}
    >
      {/* Titre du bloc (optionnel) */}
      {title && (
        <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-center mb-8">
          {title}
        </h2>
      )}

      {/* 🔥 DUAL MODE GRID SYSTEM - Desktop */}
      {/* Mode AUTO: grid-auto-rows: auto, align-items: start */}
      {/* Mode FIXED: grid-auto-rows: var height, align-items: stretch */}
      <div 
        className={`hidden md:grid modular-grid modular-grid--${gridMode}`}
        style={{
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          // Mode AUTO: minmax(auto) pour s'adapter au contenu
          // Mode FIXED: hauteurs définies ou variable CSS
          gridTemplateRows: gridMode === GRID_MODES.AUTO && !hasHeightConfig
            ? `repeat(${rows}, minmax(auto, max-content))`
            : rowHeights.length > 0 
              ? rowHeights.map(h => h > 0 ? `${h}px` : 'minmax(150px, auto)').join(' ')
              : `repeat(${rows}, minmax(150px, auto))`,
          gap: `${gap}px`,
          fontSize: 0,
          // Mode AUTO: align-items: start (contenu en haut)
          // Mode FIXED: align-items: stretch (remplir la cellule)
          alignItems: gridMode === GRID_MODES.AUTO && !hasHeightConfig ? 'start' : 'stretch',
          // Variable CSS pour row height (utilisable en CSS externe)
          '--grid-row-height': `${fixedRowHeight}px`,
          '--grid-mode': gridMode
        }}
        data-grid-mode={gridMode}
      >
        {/* 🔥 7. MAP SÉCURISÉ */}
        {cells.map((cell, index) => {
          if (!cell) return null;
          if (cell.disabled) return null;

          // Calculer l'index de ligne pour cette cellule
          const rowIndex = Math.floor(index / columns);
          const effectiveHeight = getCellHeightForRow(rowIndex);
          
          // Mode AUTO sans config: pas de contrainte de hauteur
          const isAutoMode = gridMode === GRID_MODES.AUTO && !hasHeightConfig;

          return (
            <div
              key={cell.id || index}
              className={`modular-grid__cell ${isAutoMode ? 'modular-grid__cell--auto' : 'modular-grid__cell--fixed'}`}
              style={{
                gridColumn: `span ${safeNumber(cell.colspan, 1)}`,
                gridRow: `span ${safeNumber(cell.rowspan, 1)}`,
                backgroundColor: cell.data?.background_color || 'transparent',
                padding: getPaddingStyle(cell, false),
                margin: 0,
                border: 'none',
                lineHeight: 'normal',
                borderRadius: `${cellBorderRadius}px`,
                // Mode AUTO: visible overflow, Mode FIXED: contained
                overflow: isAutoMode ? 'visible' : 'hidden',
                // Mode FIXED: structure flex pour contenu adaptatif
                ...(effectiveHeight > 0 && !isAutoMode && {
                  height: `${effectiveHeight}px`,
                  minHeight: `${effectiveHeight}px`,
                  maxHeight: `${effectiveHeight}px`,
                  // Structure flex obligatoire pour content fit
                  display: 'flex',
                  flexDirection: 'column'
                }),
                // Mode AUTO: pas de contrainte de hauteur
                ...(isAutoMode && { minHeight: 0 })
              }}
              data-testid={`grid-cell-${cell.id}`}
              data-row-index={rowIndex}
              data-cell-mode={isAutoMode ? 'auto' : 'fixed'}
            >
              {/* 🔥 CELL CONTENT WRAPPER - Mode FIXED: flex container adaptatif */}
              <div 
                className={`modular-grid__cell-content ${isAutoMode ? 'modular-grid__content--auto' : 'modular-grid__content--fixed'}`}
                style={{ 
                  fontSize: 'initial',
                  width: '100%',
                  // Mode AUTO: contenu naturel
                  ...(isAutoMode ? {
                    height: 'auto'
                  } : {
                    // Mode FIXED: wrapper flex qui prend toute la hauteur disponible
                    flex: '1 1 auto',
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    // Images: centrer, Texte: flex column
                    ...(cell.content_type === 'image' ? {
                      alignItems: 'center',
                      justifyContent: 'center'
                    } : {
                      justifyContent: 'center'
                    })
                  })
                }}
              >
                {/* 🔥 CONTENT INNER - Contenu réel avec contraintes */}
                <div
                  className={`modular-grid__content-inner ${isAutoMode ? '' : 'modular-grid__content-inner--fixed'}`}
                  style={{
                    width: '100%',
                    ...(isAutoMode ? {} : {
                      // Mode FIXED: contenu contraint
                      flex: cell.content_type === 'image' ? '0 1 auto' : '1 1 auto',
                      minHeight: 0,
                      maxHeight: '100%',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      ...(cell.content_type === 'image' ? {
                        alignItems: 'center',
                        justifyContent: 'center'
                      } : {
                        justifyContent: 'flex-start'
                      })
                    })
                  }}
                >
                  {renderCellContent(cell, rowIndex)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile Stack - Responsive */}
      <div 
        className={`md:hidden flex flex-col modular-grid modular-grid--mobile modular-grid--${gridMode}`}
        style={{
          gap: `${responsiveGap}px`,
          fontSize: 0,
          '--grid-mode': gridMode
        }}
        data-grid-mode={gridMode}
      >
        {cells.map((cell, index) => {
          if (!cell) return null;
          if (cell.disabled) return null;
          if (cell.content_type === 'empty') return null;

          // Calculer l'index de ligne pour cette cellule
          const rowIndex = Math.floor(index / columns);
          const effectiveHeight = getCellHeightForRow(rowIndex);
          // Mobile: réduire la hauteur à 80% avec minimum 80px (seulement si mode FIXED)
          const mobileHeight = effectiveHeight > 0 ? Math.max(effectiveHeight * 0.8, 80) : 0;
          
          // Mode AUTO sans config: pas de contrainte de hauteur
          const isAutoMode = gridMode === GRID_MODES.AUTO && !hasHeightConfig;

          return (
            <div
              key={cell.id || index}
              className={`modular-grid__cell ${isAutoMode ? 'modular-grid__cell--auto' : 'modular-grid__cell--fixed'}`}
              style={{
                backgroundColor: cell.data?.background_color || 'transparent',
                padding: getPaddingStyle(cell, true),
                margin: 0,
                border: 'none',
                lineHeight: 'normal',
                borderRadius: `${cellBorderRadius}px`,
                // Mode AUTO: visible overflow, Mode FIXED: contained
                overflow: isAutoMode ? 'visible' : 'hidden',
                // Mode FIXED: structure flex pour contenu adaptatif
                ...(mobileHeight > 0 && !isAutoMode && {
                  height: `${mobileHeight}px`,
                  minHeight: `${mobileHeight}px`,
                  maxHeight: `${mobileHeight}px`,
                  display: 'flex',
                  flexDirection: 'column'
                })
              }}
              data-cell-mode={isAutoMode ? 'auto' : 'fixed'}
            >
              {/* 🔥 CELL CONTENT WRAPPER - Mode FIXED: flex container adaptatif */}
              <div 
                className={`modular-grid__cell-content ${isAutoMode ? 'modular-grid__content--auto' : 'modular-grid__content--fixed'}`}
                style={{ 
                  fontSize: 'initial',
                  width: '100%',
                  // Mode AUTO: contenu naturel
                  ...(isAutoMode ? {
                    height: 'auto'
                  } : {
                    // Mode FIXED: wrapper flex qui prend toute la hauteur disponible
                    flex: '1 1 auto',
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    ...(cell.content_type === 'image' ? {
                      alignItems: 'center',
                      justifyContent: 'center'
                    } : {
                      justifyContent: 'center'
                    })
                  })
                }}
              >
                {/* 🔥 CONTENT INNER - Contenu réel avec contraintes */}
                <div
                  className={`modular-grid__content-inner ${isAutoMode ? '' : 'modular-grid__content-inner--fixed'}`}
                  style={{
                    width: '100%',
                    ...(isAutoMode ? {} : {
                      flex: cell.content_type === 'image' ? '0 1 auto' : '1 1 auto',
                      minHeight: 0,
                      maxHeight: '100%',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      ...(cell.content_type === 'image' ? {
                        alignItems: 'center',
                        justifyContent: 'center'
                      } : {
                        justifyContent: 'flex-start'
                      })
                    })
                  }}
                >
                  {renderCellContent(cell, rowIndex)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModularGridBlock;
